import { useState } from 'react'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, App, Button, Result, Skeleton } from 'antd'
import { Authorized } from '@/features/authorization/authorized.tsx'
import { useTRPC } from '@/trpc/react.ts'
import { GroupDetail } from './group-detail.tsx'
import { MoneyEntry } from './money-entry.tsx'
import { toCents } from './utils/money.ts'
import { refreshMoneyQueries } from './utils/refresh-money-queries.ts'

export function GroupReceiptPanel({ groupId }: { groupId: number }) {
  const trpc = useTRPC()
  const cache = useQueryClient()
  const { message } = App.useApp()
  const [issueOpen, setIssueOpen] = useState(false)
  const [saved, setSaved] = useState(false)
  const [revision, setRevision] = useState(0)
  const groupQuery = useQuery({
    ...trpc.bill.groupDetail.queryOptions({ groupId }),
    staleTime: Infinity,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  })
  const group = groupQuery.data
  const canReceive = group?.status === 'issued' && toCents(group.outstandingAmount) > BigInt(0)
  const billIds = group?.items.filter((item) => !item.releasedAt).map((item) => item.billId) ?? []
  const bills = useQueries({
    queries: (canReceive && groupQuery.isFetchedAfterMount && !saved ? billIds : []).map((billId) => ({
      ...trpc.bill.detail.queryOptions({ billId }),
      staleTime: Infinity,
      refetchOnMount: 'always' as const,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false
    }))
  })
  const save = useMutation(trpc.bill.groupBatchAction.mutationOptions())
  const error = groupQuery.error ?? bills.find((query) => query.error)?.error
  if (error) {
    return (
      <Alert
        className="m-6"
        type="error"
        title={error.message}
        action={
          <Button
            onClick={() => {
              void groupQuery.refetch()
              void Promise.all(bills.map((query) => query.refetch()))
            }}
          >
            重试
          </Button>
        }
      />
    )
  }
  if (!group || !groupQuery.isFetchedAfterMount) return <Skeleton active className="p-6" />
  if (saved) {
    return (
      <Result
        status="success"
        title="本组收款已登记"
        subTitle="已保留本次登记记录。待确认流水不会计入已收金额。"
        extra={
          <Button
            onClick={async () => {
              await groupQuery.refetch()
              setRevision((value) => value + 1)
              setSaved(false)
            }}
          >
            查看最新结算状态
          </Button>
        }
      />
    )
  }
  if (group.status === 'voided') return <Result title="已作废，无法调整" subTitle="该账单组已作废，不能登记收款。" />
  if (group.status === 'draft') {
    return (
      <>
        <Result
          status="info"
          title="需要确认出账后才可以登记收款"
          subTitle="该账单组仍为草稿，请先核对账单并确认出账。"
          extra={
            <Authorized permissions={['bill.group.issue']}>
              <Button type="primary" onClick={() => setIssueOpen(true)}>
                核对并确认出账
              </Button>
            </Authorized>
          }
        />
        {issueOpen && (
          <GroupDetail
            id={groupId}
            canManage
            initialAction="issue"
            onClose={() => {
              setIssueOpen(false)
              void groupQuery.refetch()
            }}
          />
        )}
      </>
    )
  }
  if (!canReceive) return <Result status="success" title="全部已结算，无法调整" subTitle="本组账单已无待收金额。" />
  if (!billIds.length) return <Alert className="m-6" type="warning" title="此账单组没有可收款账单" />
  if (bills.some((query) => !query.data || !query.isFetchedAfterMount)) return <Skeleton active className="p-6" />
  return (
    <MoneyEntry
      key={revision}
      mode="page"
      bills={bills.flatMap((query) => (query.data ? [query.data] : []))}
      initialAccountId={group.collectionAccountId ?? undefined}
      submitting={save.isPending}
      onClose={() => {}}
      onRecord={async (money) => {
        const result = await save.mutateAsync({
          action: 'receipt',
          items: [{ groupId, expectedUpdatedAt: group.updatedAt, money }]
        })
        if (!result[0]?.success) throw new Error(result[0]?.message ?? '登记失败')
      }}
      onSaved={async () => {
        setSaved(true)
        try {
          await refreshMoneyQueries(cache, trpc)
        } catch {
          void message.warning('收款已登记，刷新失败，请刷新页面查看最新状态')
        }
      }}
    />
  )
}
